"use client";

import { Comedian } from "@/data/comedians";
import { FOLLOW_LOCATIONS } from "@/data/follows";
import Follow from "@/components/checkout/Follow";
import { Loader } from "../common/icons";
import { SignInSignUpForm } from "@/components/signin";
import { SpecialRow } from "@/data/specials";
import { User } from "@supabase/supabase-js";
import { useSearchParams } from "next/navigation";
import { useShortLink } from "@/components/providers/ShortLinkProvider";

export default function Checkout({
  comedian,
  special,
  user,
}: {
  comedian: Comedian;
  special: SpecialRow;
  user?: User;
}): JSX.Element {
  const searchParams = useSearchParams();
  const optionalWelcomeBanner = searchParams.get("showWelcomeBanner");
  // Visitors who arrived via a short link (?sc=…) are following a link to *this*
  // content, so they skip the onboarding stepper and go straight to it.
  const shortLinkContext = useShortLink();
  const hasShortLinkData = !!shortLinkContext?.shortCode;

  // After subscribing, the destination is the special itself (the unlocked
  // video). New-to-platform users are first routed through the /suggested
  // onboarding stepper (routeNewUserToOnboarding → resolvePostAuthRedirect),
  // then continue on to the special; returning users (and short-link visitors)
  // go straight to it. Either way they MUST land on the content — the post-auth
  // redirects (SignInCodeForm and OnboardingStepper) call router.refresh()
  // before router.push() so the special re-renders server-side with the new
  // session and doesn't bounce them back here to /checkout.
  const redirectTo = `/${comedian.slug}/${special.slug}`;

  return (
    <>
      {optionalWelcomeBanner && !user ? (
        <div className="mt-3">
          <Loader />
        </div>
      ) : user ? (
        <Follow
          redirectTo={redirectTo}
          buttonText="Join the Guest List"
          comedianToFollow={comedian}
          followedLocation={FOLLOW_LOCATIONS.FREE_CHECKOUT_ALREADY_LOGGED_IN}
          special={special}
        />
      ) : (
        <div className="pt-3 pb-12">
          <SignInSignUpForm
            redirectTo={redirectTo}
            routeNewUserToOnboarding={!hasShortLinkData}
            buttonText="Join the Guest List"
            comedianToFollow={comedian}
            policyFooterType="comedian"
            autoFocus
          />
        </div>
      )}
    </>
  );
}
